import React, { useState, useEffect, useRef } from "react";
import TabSwitch from "./TabSwitch";

import "./ChessClock.css";

enum GameState {
  Stopped = "stopped",
  Running = "running",
  Paused = "paused",
}

enum GameMode {
  Normal = "normal",
  Blitz = "blitz",
}

interface PlayerState {
  id: number;
  isActive: boolean;
  name: string;
  timeLeft: number;
}

interface Player {
  name: string;
  timeLeft: number;
}

const useWakeLock = () => {
  const wakeLockRef = useRef<any>(null);

  const requestWakeLock = async () => {
    try {
      wakeLockRef.current = await navigator.wakeLock?.request("screen");
      console.log("Wake Lock active");

      document.addEventListener("visibilitychange", async () => {
        if (wakeLockRef.current && document.visibilityState === "visible") {
          try {
            wakeLockRef.current = await navigator.wakeLock?.request("screen");
          } catch (err) {
            console.error("Reacquire failed:", err);
          }
        }
      });
    } catch (err: any) {
      console.error("Wake Lock failed:", err.name, err.message);
    }
  };

  useEffect(() => {
    // Optional cleanup on unmount
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    };
  }, []);

  return { requestWakeLock };
};

const useGameState = () => {
  const [mode, setMode] = useState(GameMode.Normal);
  const [state, setState] = useState<GameState>(GameState.Stopped);
  const [currentPlayer, setCurrentPlayer] = useState<number | null>(null);
  const [availableTime, setAvailableTime] = useState(60 * 1000);

  const [players, setPlayers] = useState<Player[]>([
    { name: "Player 1", timeLeft: availableTime },
    { name: "Player 2", timeLeft: availableTime },
  ]);

  const setPlayerTimes = (time: number) => {
    setAvailableTime(time * 1000);
    setPlayers((prev) => prev.map((p) => ({ ...p, timeLeft: time * 1000 })));
  };

  const switchPlayer = () => {
    if (mode === GameMode.Blitz) {
      setPlayers((prev) =>
        prev.map((p, i) =>
          i === currentPlayer ? { ...p, timeLeft: availableTime } : p
        )
      );
    }

    setCurrentPlayer((prev) => {
      if (prev === 0) return 1;
      if (prev === 1) return 0;

      return null;
    });
  };

  useEffect(() => {
    if (state !== GameState.Running) return;
    if (currentPlayer === undefined) return;

    const interval = setInterval(() => {
      setPlayers((prev) => {
        return prev.map((player, i) => {
          if (currentPlayer !== i) return player;

          const newTime = player.timeLeft - 1000;

          if (newTime <= 0) {
            if (mode === GameMode.Normal) {
              setState(GameState.Stopped);
              setCurrentPlayer(null);
            }

            return { ...player, timeLeft: 0 };
          }

          return { ...player, timeLeft: newTime };
        });
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [currentPlayer, state, mode]);

  return {
    players: players.map((p, id) => {
      return { ...p, id, isActive: currentPlayer === id };
    }),
    currentPlayer,
    state,
    mode,
    setMode,
    switchPlayer,
    setState,
    setPlayers,
    setPlayerTimes,
    setCurrentPlayer,
  };
};

function ChessClock() {
  const game = useGameState();
  const { requestWakeLock } = useWakeLock();

  const [time, setTime] = useState(10);

  const pauseGame = () => {
    if (game.state === GameState.Running) {
      game.setState(GameState.Paused);
    }
  };

  const resumeGame = () => {
    if (game.state === GameState.Paused) {
      game.setState(GameState.Running);
    }
  };

  const stopGame = () => {
    game.setState(GameState.Stopped);
    game.setCurrentPlayer(null);
  };

  const resetGame = () => {
    game.setCurrentPlayer(null);
    game.setPlayerTimes(time);
  };

  const handlePlayerClick = (playerId: number) => {
    requestWakeLock();

    if (game.currentPlayer === null) {
      game.setCurrentPlayer(playerId);
    }

    if (game.currentPlayer !== null && game.currentPlayer !== playerId) return;

    if (game.state !== GameState.Running) {
      game.setState(GameState.Running);
    }

    game.switchPlayer();
  };

  const setStartTime = (time: number) => {
    setTime(time);
    game.setPlayerTimes(time);
  };

  return (
    <div className="chess-clock">
      <PlayerButton
        player={game.players[1]}
        onClick={() => handlePlayerClick(1)}
      />

      <div className="center-controls">
        {game.state === GameState.Stopped && (
          <>
            <TabSwitch<GameMode>
              ariaLabel="Mode"
              className="control-tabs"
              optionClassName="control-tab-option"
              selectedClassName="selected"
              items={[
                { value: GameMode.Normal, label: "Normal" },
                { value: GameMode.Blitz, label: "Blitz" },
              ]}
              selected={game.mode}
              onChange={(value) => game.setMode(value)}
            />

            {game.mode === GameMode.Blitz && (
              <TabSwitch<number>
                ariaLabel="Time per turn"
                className="control-tabs"
                optionClassName="control-tab-option"
                selectedClassName="selected"
                items={[
                  { value: 5, label: "5s" },
                  { value: 10, label: "10s" },
                  { value: 15, label: "15s" },
                  { value: 20, label: "20s" },
                  { value: 30, label: "30s" },
                ]}
                selected={time}
                onChange={(time) => setStartTime(time)}
              />
            )}

            {game.mode === GameMode.Normal && (
              <TabSwitch<number>
                ariaLabel="Time preset"
                className="control-tabs"
                optionClassName="control-tab-option"
                selectedClassName="selected"
                items={[
                  { value: 1 * 60, label: "1m" },
                  { value: 3 * 60, label: "3m" },
                  { value: 5 * 60, label: "5m" },
                  { value: 10 * 60, label: "10m" },
                  { value: 15 * 60, label: "15m" },
                ]}
                selected={time}
                onChange={(time) => setStartTime(time)}
              />
            )}
          </>
        )}

        {game.state === GameState.Running && (
          <>
            <button className="control-btn" onClick={pauseGame}>
              Pause
            </button>
          </>
        )}
        {game.state === GameState.Paused && (
          <>
            <button className="control-btn" onClick={resetGame}>
              Reset
            </button>
            <button className="control-btn" onClick={resumeGame}>
              Resume
            </button>
            <button className="control-btn" onClick={stopGame}>
              Stop
            </button>
          </>
        )}
      </div>

      <PlayerButton
        player={game.players[0]}
        onClick={() => handlePlayerClick(0)}
      />
    </div>
  );
}

function PlayerButton(props: { player: PlayerState; onClick: () => void }) {
  const LOW_TIME_MS = 10000;

  const { player } = props;

  const classNames: string[] = [
    "player",
    player.id === 0 ? "player-1" : "player-2",
    player.isActive ? "active" : "inactive",
  ];

  if (player.timeLeft < LOW_TIME_MS) {
    classNames.push("low-time");
  }

  return (
    <div className={classNames.join(" ")} onClick={props.onClick}>
      <div className="player-content">
        <div className="player-name">{props.player.name}</div>
        <div className="player-time">{formatTime(props.player.timeLeft)}</div>
      </div>
    </div>
  );
}

function formatTime(milliseconds: number): string {
  const totalSeconds = Math.ceil(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

export default ChessClock;
